import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import PostCard from "../components/PostCard";
import AdSlot from "../components/AdSlot";
import FoundingBanner from "../components/FoundingBanner";
import SpotlightBanner from "../components/SpotlightBanner";
import RatingRangeFilter from "../components/RatingRangeFilter";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Sparkles, Flame, Users } from "lucide-react";

export default function Home() {
    const { user } = useAuth();
    const [posts, setPosts] = useState([]);
    const [sort, setSort] = useState("recent");
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState({ min: 1, max: 10 });

    const load = async (s, r = range) => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ sort: s });
            if (s === "top" && (r.min > 1 || r.max < 10)) {
                params.set("min_rating", r.min);
                params.set("max_rating", r.max);
            }
            const { data } = await api.get(`/posts?${params.toString()}`);
            setPosts(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(sort, range); }, [sort, range]);

    return (
        <main className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
            <section className="mb-8 sm:mb-12" data-testid="hero-section">
                <div className="inline-block bg-[#FFE55C] border-2 border-[#0A0A0A] px-3 py-1 rounded-full text-xs font-black tracking-wider uppercase mb-4">
                    Rate. Post. Repeat.
                </div>
                <h1 className="display text-4xl sm:text-6xl font-black leading-[0.95] mb-3">
                    A <span className="serif font-normal">Toe-tal</span> place to<br />
                    rate <span className="bg-[#FFB8D1] px-2 border-2 border-[#0A0A0A] rounded-lg inline-block">feet</span> 1&nbsp;to&nbsp;10.
                </h1>
                <p className="text-base sm:text-lg text-[#4A4A4A] font-medium max-w-lg">
                    Share your best angle, get honest ratings, and follow the pod. Strictly SFW — read the house rules before you post.
                </p>
                {!user && (
                    <div className="flex gap-3 mt-5">
                        <Link to="/register" className="px-5 py-3 rounded-lg brut bg-[#0A0A0A] text-white font-bold" data-testid="hero-cta-signup">
                            Create profile
                        </Link>
                        <Link to="/leaderboard" className="px-5 py-3 rounded-lg brut-inset bg-white font-bold" data-testid="hero-cta-leaderboard">
                            See top rated
                        </Link>
                    </div>
                )}
            </section>

            {(!user || user.tier === "free") && <FoundingBanner />}
            <SpotlightBanner />

            <div className="flex items-center gap-2 mb-6 flex-wrap" data-testid="feed-tabs">
                <button
                    onClick={() => setSort("recent")}
                    className={`px-4 py-2 rounded-lg border-2 border-[#0A0A0A] font-bold text-sm inline-flex items-center gap-1.5 ${
                        sort === "recent" ? "bg-[#FFB8D1] shadow-[3px_3px_0px_#0A0A0A]" : "bg-white"
                    }`}
                    data-testid="feed-tab-recent"
                >
                    <Sparkles className="w-4 h-4" /> Fresh
                </button>
                <button
                    onClick={() => setSort("top")}
                    className={`px-4 py-2 rounded-lg border-2 border-[#0A0A0A] font-bold text-sm inline-flex items-center gap-1.5 ${
                        sort === "top" ? "bg-[#FFE55C] shadow-[3px_3px_0px_#0A0A0A]" : "bg-white"
                    }`}
                    data-testid="feed-tab-top"
                >
                    <Flame className="w-4 h-4" /> Top rated
                </button>
                {user && (
                    <button
                        onClick={() => setSort("following")}
                        className={`px-4 py-2 rounded-lg border-2 border-[#0A0A0A] font-bold text-sm inline-flex items-center gap-1.5 ${
                            sort === "following" ? "bg-[#B8E1FF] shadow-[3px_3px_0px_#0A0A0A]" : "bg-white"
                        }`}
                        data-testid="feed-tab-following"
                    >
                        <Users className="w-4 h-4" /> Following
                    </button>
                )}
            </div>

            {sort === "top" && (
                <div className="mb-6">
                    <RatingRangeFilter min={range.min} max={range.max} onChange={setRange} />
                </div>
            )}

            <div className="space-y-8" data-testid="feed-list">
                {loading ? (
                    <div className="text-center py-16 font-bold text-[#4A4A4A]">Loading…</div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-16 border-2 border-dashed border-[#0A0A0A] rounded-xl bg-white/60" data-testid="feed-empty">
                        <div className="font-black text-lg mb-1">{sort === "following" ? "Your following feed is empty" : "No posts yet"}</div>
                        <div className="text-sm text-[#4A4A4A] font-medium">
                            {sort === "following" ? "Follow some posters to see their fresh posts here." : "Be the first to post — the pod awaits."}
                        </div>
                        {user && sort !== "following" && (
                            <Link to="/upload" className="mt-4 inline-block px-4 py-2 rounded-lg brut bg-[#FFB8D1] font-bold" data-testid="empty-upload-cta">
                                Post the first pic
                            </Link>
                        )}
                    </div>
                ) : (
                    posts.map((p, i) => (
                        <React.Fragment key={p.id}>
                            <PostCard post={p} />
                            {i === 2 && <AdSlot />}
                            {i === 8 && <AdSlot />}
                        </React.Fragment>
                    ))
                )}
            </div>
        </main>
    );
}
